import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPieChart } from 'react-icons/fi';
import styles from './WalletBadge.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';
import CurrencySelector from '../CurrencySelector/CurrencySelector';

interface WalletRow { currency: string; available: number; locked: number; held?: number; withdrawable?: number; }

const WalletBadge: React.FC = () => {
  const location = useLocation();
  const [wallets, setWallets] = useState<WalletRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const { format, convertFromNative, selected } = useCurrency();

  const load = () => {
    setLoading(true);
    axiosClient.get('/funds/balance')
      .then(({ data }) => setWallets(data.wallets || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Also refresh instantly whenever a deposit/withdrawal/cancel happens anywhere in the app.
    window.addEventListener('wallet:refresh', load);
    return () => window.removeEventListener('wallet:refresh', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Each currency's own balance is only ever converted here, live, for display — never stored
  // pre-converted. This is the exact "fetch wallet → fetch rates → convert → sum portfolio" flow.
  const portfolioTotal = (wallets || []).reduce((sum, w) => sum + convertFromNative(w.available, w.currency), 0);
  const breakdown = (wallets || []).filter((w) => w.available > 0 || w.locked > 0);
  // Part of the wallet cannot be withdrawn yet (Portal principal, ROI / override bonus inside 30 days).
  const withdrawableTotal = (wallets || []).reduce((sum, w) => sum + convertFromNative(w.withdrawable ?? w.available, w.currency), 0);
  const hasHeld = (wallets || []).some((w) => (w.held ?? 0) > 0);

  const tooltip = breakdown.length
    ? `Wallet by currency: ${breakdown.map((w) => `${w.available.toLocaleString()} ${w.currency}${(w.held ?? 0) > 0 ? ` (${(w.held ?? 0).toLocaleString()} on hold)` : ''}${w.locked > 0 ? ` (+${w.locked.toLocaleString()} reserved for withdrawal)` : ''}`).join(', ')}`
    : 'Available wallet balance';

  return (
    <div className={styles.group}>
      <Link to="/dashboard/funds" className={styles.badge} title={tooltip}>
        <FiPieChart className={styles.icon} />
        <div className={styles.text}>
          <span className={styles.label}>Wallet</span>
          <b className={styles.amount}>
            {loading || wallets === null ? '…' : format(portfolioTotal)}
          </b>
          {hasHeld && !loading && wallets !== null && (
            <span className={styles.rateNote}>Withdrawable {format(withdrawableTotal)}</span>
          )}
          {selected && breakdown.length > 1 && <span className={styles.rateNote}>{breakdown.length} currencies</span>}
        </div>
      </Link>
      <CurrencySelector />
    </div>
  );
};

export default WalletBadge;